import './AurigaGrid.scss';

import { useAgGridTheme } from '@hooks/useAgGridTheme';
import Box from '@mui/material/Box';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { Ref } from 'react';

interface AurigaGridProps<TData = unknown> extends Omit<AgGridReactProps<TData>, 'theme'> {
  ref?: Ref<AgGridReact<TData>>;
}

/**
 * Auriga's AG Grid wrapper that automatically applies the correct theme
 * and dark mode support based on the current color scheme.
 *
 * Uses AG Grid's theme modes — sets `data-ag-theme-mode` on a wrapper div
 * so AG Grid switches colour params without a React re-render.
 *
 * The theme is enforced centrally — consumers cannot override it.
 * Supports generics for type-safe row data.
 *
 * @example
 * ```tsx
 * const gridRef = useRef<AurigaGridRef>(null);
 * <AurigaGrid<MyRowType>
 *   ref={gridRef}
 *   rowData={data}
 *   columnDefs={columns}
 *   {...globalGridDefaults}
 * />
 * ```
 */
export const AurigaGrid = <TData,>({ ref, ...props }: AurigaGridProps<TData>) => {
  const { gridTheme, gridThemeMode } = useAgGridTheme();

  return (
    <Box className='ag-theme-mode' data-ag-theme-mode={gridThemeMode}>
      <AgGridReact<TData> {...props} ref={ref} theme={gridTheme} />
    </Box>
  );
};

/** Ref type for accessing the AG Grid API via AurigaGrid */
export type AurigaGridRef<TData = unknown> = AgGridReact<TData>;

export default AurigaGrid;
